import { ErrorSource, RawError } from '@datadog/browser-core';
import { datadogRum } from '@datadog/browser-rum';
import { AUSSIE_IS_SCRIPTS, LENDI_IS_SCRIPTS } from '@lendi-ui/scripts';
import { Brand, useSession } from '@lendi/lala-react';
import { SimpleNavbar } from '@lendi/navbar';
import { TeamSummaryNavigation } from '@lendi/team-navigation';
import { AuthGuard } from 'components/AuthGuard';
import { ErrorBoundary } from 'components/ErrorReporter';
import LaunchDarklyConsumerProvider from 'components/LaunchDarkly';
import { AppointmentDetailsContextProvider } from 'contexts/AppointmentDetailsContext';
import { TimezoneContextProvider } from 'contexts/TimezoneContext';
import { useFlags, withLDProvider } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { SWRConfig } from 'swr';
import { getCustomerById, User } from '../api';
import { AvailableQueryParamKeys, useGetQueryParams } from '../hooks/useGetQueryParams';
import { URL_PARAM_CUSTOMER_ID } from '../utils/enums/';
import { trackingService } from '../utils/helpers/tracking';
import { LayoutStyled } from './index.style';

const AppointmentType = React.lazy(() => import('pages/appointment-type'));
const TimeSelection = React.lazy(() => import('pages/time-selection'));
const Process = React.lazy(() => import('pages/process'));
const Confirmation = React.lazy(() => import('pages/confirmation'));
const NoAvailability = React.lazy(() => import('pages/no-availability'));
const SelfBookTimeSelection = React.lazy(() => import('pages/team-time-selection/self-book'));
const SomeoneElseTimeSelection = React.lazy(() => import('pages/team-time-selection/someone-else'));
const SomeoneSpecificTimeSelection = React.lazy(() => import('pages/team-time-selection/someone-specific'));
const Success = React.lazy(() => import('pages/success'));
const AppointmentNotes = React.lazy(() => import('pages/appointment-notes'));
const Appointments = React.lazy(() => import('pages/appointments'));
const BAARoute = '/book-appointment/';
const TeamviewRoute = `/team-view/customer/:${URL_PARAM_CUSTOMER_ID}/book-appointment/`;

const handleNavBarException = (error: Error) => {
  (error as unknown as RawError).source = ErrorSource.SOURCE;
  datadogRum.addError(error);
};

export const BAARoutes = () => {
  const { appointmentTypePageFlag, acCancelReschedule } = useFlags();
  const session = useSession();
  const [customerID, setCustomerID] = useState<string>('');
  const [customer, setCustomer] = useState<User | null>(null);
  const isIframeMode = useGetQueryParams(AvailableQueryParamKeys.CV_IFRAME_MODE) === 'true';

  const loadCustomer = async (id: string): Promise<void> => {
    const customer = await getCustomerById(id);
    setCustomer(customer);
  };

  useEffect(() => {
    loadCustomer(customerID);
  }, [customerID]);

  const handleLogout = () => {
    session.logout();
  };

  return (
    <>
      {/* CUSTOMER VIEW for Lendi */}
      <Route
        path={BAARoute}
        render={({ match }) => {
          return (
            <>
              {!isIframeMode && (
                <SimpleNavbar
                  showContinueAction={false}
                  showTalkToExpertCta={false}
                  showBookanAppointmentCta={false}
                  onException={(error) => handleNavBarException(error)}
                  onLogout={handleLogout}
                  homeURL={'/'}
                />
              )}
              <Route path={`${match.path}`} exact component={AppointmentType}>
                {appointmentTypePageFlag === false && <Redirect to={`${match.path}time-selection`} />}
              </Route>
              <Route path={`${match.path}time-selection`} exact component={TimeSelection} />
              <Route path={`${match.path}process`} exact component={Process} />
              <Route path={`${match.path}confirmation`} exact component={Confirmation} />
              <Route path={`${match.path}no-availability`} exact component={NoAvailability} />
            </>
          );
        }}
      />
      {/* TEAM VIEW */}
      <Route
        path={TeamviewRoute}
        render={({ match, location }) => {
          const { [URL_PARAM_CUSTOMER_ID]: customerId } = match.params;

          if (customerId) {
            setCustomerID(customerId);
          }

          const teamNavCustomer = customer
            ? {
                customerId: customer?.id || '',
                name: customer?.firstName || '',
                phone: customer?.mobileNumber || '',
                email: customer?.email || '',
              }
            : undefined;

          return (
            <TeamSummaryNavigation
              showCommsPanel
              onException={(error: Error) => datadogRum.addError(error)}
              customers={teamNavCustomer ? [teamNavCustomer] : []}
              // This should never happen because currently we only have 1 customer given to the minimal navbar at any time
              onSwitchCustomer={() => undefined}
              selectedCustomer={teamNavCustomer}
            >
              <LayoutStyled>
                {/*TODO: Once all team view BAA released, then remove the next line Route for self-book */}
                {acCancelReschedule && <Route path={`${match.path}`} exact component={Appointments} />}
                <Route path={`${match.path}myself`} exact component={SelfBookTimeSelection} />
                {/*TODO: Update third party and some specific urls to a correct or based on some data from team.lendi portal */}
                <Route path={`${match.path}third-party`} exact component={SomeoneElseTimeSelection} />
                <Route path={`${match.path}someone-specific`} exact component={SomeoneSpecificTimeSelection} />
                <Route path={`${match.path}notes`} exact component={AppointmentNotes} />
                <Route
                  path={`${match.path}success`}
                  exact
                  component={() => (
                    <Success
                      pageTitle={`Success`}
                      pageDescription={`The appointment has been booked and a confirmation email has been sent to the customer and team member.`}
                      hasBackButton
                    />
                  )}
                />
                <Route
                  path={`${match.path}hot-transfer-success`}
                  exact
                  component={() => (
                    <Success
                      pageTitle={`Hot transfer confirmed`}
                      pageDescription={`The appointment has been booked.`}
                    />
                  )}
                />
              </LayoutStyled>
            </TeamSummaryNavigation>
          );
        }}
      />
    </>
  );
};

const App: React.FC = () => {
  const { brand, environment } = useSession();
  let { pathname } = useLocation();
  const { appointmentAuthExperiment } = useFlags();

  const mapBrandToName = {
    [Brand.Lendi]: 'Lendi',
    [Brand.Aussie]: 'Aussie',
  };

  React.useEffect(() => {
    trackingService.trackPage('Scaffold');
  }, [pathname]);

  return (
    <ErrorBoundary>
      <Helmet>
        <title>{mapBrandToName[brand]}</title>
        {brand === Brand.Lendi ? LENDI_IS_SCRIPTS[environment] : AUSSIE_IS_SCRIPTS[environment]}
      </Helmet>
      <SWRConfig
        value={{
          revalidateOnFocus: false,
          revalidateOnReconnect: true,
          shouldRetryOnError: false,
          onError: (error) => {
            (error as unknown as RawError).source = ErrorSource.SOURCE;
            datadogRum.addError(error);
          },
          revalidateOnMount: true,
        }}
      >
        <React.Suspense fallback={null}>
          <Switch>
            <LaunchDarklyConsumerProvider>
              <TimezoneContextProvider>
                <AppointmentDetailsContextProvider>
                  {appointmentAuthExperiment === undefined ? null : appointmentAuthExperiment ? (
                    <BAARoutes />
                  ) : (
                    <AuthGuard>
                      <BAARoutes />
                    </AuthGuard>
                  )}
                </AppointmentDetailsContextProvider>
              </TimezoneContextProvider>
            </LaunchDarklyConsumerProvider>
          </Switch>
        </React.Suspense>
      </SWRConfig>
    </ErrorBoundary>
  );
};

export default withLDProvider({ clientSideID: process.env.REACT_APP_LAUNCH_DARKLY_CLIENT_ID || '' })(App);
